import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import Modal from '@atoms/Modal/Modal';
import NavButton from '@atoms/NavButton/NavButton';
import { ILightBoxImage } from '@models/ILightBoxImage';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import ErrorBannerNotificationEnum from '@models/enums/ErrorBannerNotificationEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import LightBoxInner from '@molecules/LightBoxInner/LightBoxInner';
import getLightBoxImages from '@services/getLightBoxImages';
import useErrorNotificationStore from '@state/ErrorBannerNotificationDisplayStore';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly contentIds: string[];
  readonly initialIndex?: number;
  readonly language?: string;
  readonly onClose: () => void;
}

const LightBoxOuter = ({
  contentIds,
  initialIndex = 0,
  language,
  onClose,
}: Props) => {
  const isMobileViewport = useMediaQuery(768);
  const isMobileScreen = useMediaQuery(768, BreakpointTypeEnum.Screen);
  const isBelowLargeDesktop = useMediaQuery(1440);
  const triggeredRequest = useRef(false);
  const [isEnlargedImage, setIsEnlargedImage] = useState(false);
  const [lightBoxImages, setLightBoxImages] = useState<ILightBoxImage[]>();
  const navButtonId = 'lightbox-carousel';
  const hasMultipleSlides = lightBoxImages && lightBoxImages?.length > 1;

  const { setErrorBannerNotification } = useErrorNotificationStore();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const getImages = async () => {
      try {
        const response = await getLightBoxImages({ contentIds, language });
        setLightBoxImages(response);
      } catch {
        onClose();
        setErrorBannerNotification(
          ErrorBannerNotificationEnum.LightboxOuterImages
        );
      }
    };

    if (!triggeredRequest.current) {
      getImages();
      triggeredRequest.current = true;
    }
  }, [contentIds, language, setLightBoxImages]);

  return (
    <Modal
      contentClasses="py-0 h-full"
      crossColor="white"
      isOpen
      modalClasses="max-w-full px-0"
      setIsOpen={onClose}
      isCloseButtonFixed
    >
      {lightBoxImages ? (
        <div className="relative flex w-full items-center">
          {hasMultipleSlides && (
            <motion.div
              layout
              className={classNames('absolute z-10', {
                hidden: isMobileScreen,
                'top-5 left-5': isMobileViewport,
                'left-[5%]': !isMobileViewport && isBelowLargeDesktop,
                'left-[10%]': !isMobileViewport && !isBelowLargeDesktop,
              })}
            >
              <NavButton
                direction={HorizontalDirectionEnum.Left}
                id={navButtonId}
              />
            </motion.div>
          )}

          <Carousel
            breakpoints={{
              1439: {
                spaceBetween: 140,
              },
            }}
            autoHeight
            enabled={hasMultipleSlides}
            initialSlide={initialIndex}
            loop
            className="w-full"
            touchStartPreventDefault={false}
            navId={navButtonId}
            slidesPerView={1}
            spaceBetween={20}
          >
            {lightBoxImages.map((lightBoxImage) => (
              <SwiperSlide
                key={lightBoxImage.meta?.contentLink}
                className="flex justify-center lg:items-center"
              >
                <LightBoxInner
                  hasMultipleSlides={!!hasMultipleSlides}
                  isEnlargedImage={isEnlargedImage}
                  setIsEnlargedImage={setIsEnlargedImage}
                  {...lightBoxImage}
                />
              </SwiperSlide>
            ))}
          </Carousel>

          {hasMultipleSlides && (
            <motion.div
              layout
              className={classNames('absolute z-10', {
                hidden: isMobileScreen,
                'top-5 left-[85px]': isMobileViewport,
                'right-[5%]': !isMobileViewport && isBelowLargeDesktop,
                'right-[10%]': !isMobileViewport && !isBelowLargeDesktop,
              })}
            >
              <NavButton
                direction={HorizontalDirectionEnum.Right}
                id={navButtonId}
              />
            </motion.div>
          )}
        </div>
      ) : (
        <LoadingSpinner extraClasses="h-full" />
      )}
    </Modal>
  );
};

export default LightBoxOuter;
